/**
 * Custom Table Component
 * Enhanced Ant Design Table with custom features
 */

import { Table as AntTable, TableProps as AntTableProps } from 'antd';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import './CustomTable.scss';
import { CustomButton } from '../CustomButton';

export interface CustomTableProps<T> extends AntTableProps<T> {
  /** Enable export functionality */
  exportable?: boolean;
  /** Export function callback */
  onExport?: () => void;
  /** Enable refresh functionality */
  refreshable?: boolean;
  /** Refresh function callback */
  onRefresh?: () => void;
  /** Show row actions column */
  showActions?: boolean;
  /** Table title */
  tableTitle?: string;
  /** Custom toolbar actions */
  toolbarActions?: React.ReactNode;
}

/**
 * CustomTable Component
 * 
 * @example
 * ```tsx
 * <CustomTable
 *   columns={columns}
 *   dataSource={data}
 *   exportable
 *   refreshable
 *   onExport={handleExport}
 *   onRefresh={handleRefresh}
 * />
 * ```
 */
export function CustomTable<T extends object>({
  exportable = false,
  onExport,
  refreshable = false,
  onRefresh,
  tableTitle,
  toolbarActions,
  className,
  ...rest
}: CustomTableProps<T>) {
  const tableClasses = classNames('custom-table', className);

  return (
    <div className="custom-table-wrapper">
      {(tableTitle || toolbarActions || exportable || refreshable) && (
        <div className="custom-table-toolbar">
          {tableTitle && <h3 className="custom-table-toolbar__title">{tableTitle}</h3>}
          
          <div className="custom-table-toolbar__actions">
            {toolbarActions}
            
            {refreshable && (
              <CustomButton
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                variant="ghost"
                size="small"
              >
                Refresh
              </CustomButton>
            )}
            
            {exportable && (
              <CustomButton
                icon={<ExportOutlined />}
                onClick={onExport}
                variant="ghost"
                size="small"
              >
                Export
              </CustomButton>
            )}
          </div>
        </div>
      )}

      <AntTable<T>
        {...rest}
        className={tableClasses}
        scroll={{ x: 'max-content', ...rest.scroll }}
      />
    </div>
  );
}

export default CustomTable;

